import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Paper,
  Drawer,
  Divider,
} from '@mui/material';
import { Send, Psychology, Check, Refresh, Close, ChevronRight, RestartAlt } from '@mui/icons-material';
import { apiService } from '../services/api';
import { useBrandColors } from '../hooks/useBrandColors';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface SuggestedPlan {
  // New format
  add?: Array<{
    name: string;
    startDate: string;
    endDate: string;
    status?: string;
  }>;
  remove?: string[];
  update?: Array<{
    id: string;
    name?: string;
    startDate?: string;
    endDate?: string;
    status?: string;
  }>;
  // Legacy format
  phases?: Array<{
    name: string;
    startDate: string;
    endDate: string;
    status: string;
  }>;
  keystones?: Array<{
    name: string;
    date: string;
    type: string;
  }>;
}

interface AIPlanningChatProps {
  projectId: string;
  projectName: string;
  onClose: () => void;
  onPlanAccepted: () => void;
}

export const AIPlanningChat: React.FC<AIPlanningChatProps> = ({
  projectId,
  projectName,
  onClose,
  onPlanAccepted,
}) => {
  const brandColors = useBrandColors();
  // Load saved messages from localStorage
  const savedMessages = localStorage.getItem(`ai-chat-${projectId}`);
  const initialMessages = savedMessages ? JSON.parse(savedMessages) : [];
  
  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [context, setContext] = useState<any>(null);
  const [suggestedPlan, setSuggestedPlan] = useState<SuggestedPlan | null>(null);
  const [acceptingPlan, setAcceptingPlan] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Save messages to localStorage when they change
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem(`ai-chat-${projectId}`, JSON.stringify(messages));
    }
  }, [messages, projectId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load context and start conversation (only if no saved messages)
  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        const ctx = await apiService.getAIPlanningContext(projectId);
        setContext(ctx);
        
        // Only send initial message if no saved messages
        if (messages.length === 0) {
          const response = await apiService.sendAIPlanningMessage(
            projectId,
            [], // Empty messages to get AI to start
            ctx
          );
          
          setMessages([{ role: 'assistant', content: response.message }]);
          if (response.suggestedPlan) {
            setSuggestedPlan(response.suggestedPlan);
          }
        }
      } catch (error) {
        console.error('Failed to initialize AI chat:', error);
        if (messages.length === 0) {
          setMessages([{
            role: 'assistant',
            content: 'Hej! Jag är redo att hjälpa dig planera detta projekt. Beskriv vad projektet handlar om så kan vi börja!'
          }]);
        }
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [projectId]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage: Message = { role: 'user', content: input };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    try {
      const response = await apiService.sendAIPlanningMessage(
        projectId,
        newMessages,
        context
      );

      setMessages([...newMessages, { role: 'assistant', content: response.message }]);
      if (response.suggestedPlan) {
        setSuggestedPlan(response.suggestedPlan);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      setMessages([...newMessages, {
        role: 'assistant',
        content: 'Ursäkta, något gick fel. Försök igen.'
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptPlan = async () => {
    if (!suggestedPlan) return;

    setAcceptingPlan(true);
    try {
      // Support both new format (add/remove/update) and legacy (phases)
      await apiService.acceptAIPlan(projectId, {
        add: suggestedPlan.add || suggestedPlan.phases,
        remove: suggestedPlan.remove || [],
        update: suggestedPlan.update || [],
        keystones: suggestedPlan.keystones,
      });
      onPlanAccepted();
    } catch (error) {
      console.error('Failed to accept plan:', error);
      setMessages([...messages, {
        role: 'assistant',
        content: 'Kunde inte spara planen. Försök igen.'
      }]);
    } finally {
      setAcceptingPlan(false);
    }
  };

  const handleRegeneratePlan = async () => {
    setSuggestedPlan(null);
    const regenerateMessage: Message = {
      role: 'user',
      content: 'Kan du föreslå en annan plan med andra datum eller faser?'
    };
    setMessages([...messages, regenerateMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await apiService.sendAIPlanningMessage(
        projectId,
        [...messages, regenerateMessage],
        context
      );

      setMessages([...messages, regenerateMessage, { role: 'assistant', content: response.message }]);
      if (response.suggestedPlan) {
        setSuggestedPlan(response.suggestedPlan);
      }
    } catch (error) {
      console.error('Failed to regenerate:', error);
    } finally {
      setLoading(false);
    }
  };

  // Start new conversation - clear localStorage and reload context
  const handleNewConversation = async () => {
    // Clear localStorage
    localStorage.removeItem(`ai-chat-${projectId}`);
    setSuggestedPlan(null);
    setMessages([]);
    setLoading(true);

    try {
      // Reload fresh context
      const ctx = await apiService.getAIPlanningContext(projectId);
      setContext(ctx);
      
      // Send initial message with fresh context
      const response = await apiService.sendAIPlanningMessage(
        projectId,
        [], // Empty messages to get AI to start fresh
        ctx
      );
      
      setMessages([{ role: 'assistant', content: response.message }]);
      if (response.suggestedPlan) {
        setSuggestedPlan(response.suggestedPlan);
      }
    } catch (error) {
      console.error('Failed to start new conversation:', error);
      setMessages([{
        role: 'assistant',
        content: 'Hej! Jag är redo att hjälpa dig planera detta projekt. Beskriv vad projektet handlar om så kan vi börja!'
      }]);
    } finally {
      setLoading(false);
    }
  };

  // Strip JSON from display message
  const cleanMessage = (content: string) => {
    return content.replace(/```json[\s\S]*?```/g, '').trim();
  };

  return (
    <Drawer
      anchor="right"
      open={true}
      onClose={onClose}
      variant="persistent"
      sx={{
        '& .MuiDrawer-paper': {
          width: 380,
          boxShadow: '-4px 0 20px rgba(0,0,0,0.1)',
        },
      }}
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#fff',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            backgroundColor: '#9b59b6',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Psychology sx={{ fontSize: 28 }} />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                AI Projektplanerare
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {projectName}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton 
              size="small" 
              onClick={handleNewConversation} 
              sx={{ color: '#fff', opacity: 0.9, '&:hover': { opacity: 1 } }}
              title="Ny konversation"
            >
              <RestartAlt />
            </IconButton>
            <IconButton size="small" onClick={onClose} sx={{ color: '#fff' }}>
              <ChevronRight />
            </IconButton>
          </Box>
        </Box>

        {/* Context Info */}
        {context && (
          <Box sx={{ p: 1.5, backgroundColor: '#f8f9fa', borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="caption" color="textSecondary">
              📊 {context.timeEntries?.totalHours || 0}h loggat • 
              👥 {context.teamMembers?.length || 0} teammedlemmar •
              📁 {context.existingPhases?.length || 0} faser
            </Typography>
          </Box>
        )}

        {/* Messages */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {messages.map((msg, idx) => (
            <Box
              key={idx}
              sx={{
                display: 'flex',
                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  maxWidth: '90%',
                  backgroundColor: msg.role === 'user' ? '#9b59b6' : '#f5f5f5',
                  color: msg.role === 'user' ? '#fff' : 'inherit',
                  borderRadius: 2,
                  borderTopRightRadius: msg.role === 'user' ? 0 : 16,
                  borderTopLeftRadius: msg.role === 'assistant' ? 0 : 16,
                }}
              >
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>
                  {cleanMessage(msg.content)}
                </Typography>
              </Paper>
            </Box>
          ))}

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Paper
                elevation={0}
                sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}
              >
                <CircularProgress size={20} />
              </Paper>
            </Box>
          )}

          <div ref={messagesEndRef} />
        </Box>

        {/* Suggested Plan Actions */}
        {suggestedPlan && (
          <Box
            sx={{
              p: 2,
              borderTop: '1px solid #e0e0e0',
              backgroundColor: '#f0f7ff',
            }}
          >
            <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1.5 }}>
              ✨ AI har föreslagit ändringar:
            </Typography>
            <Box sx={{ mb: 1.5, maxHeight: 150, overflowY: 'auto' }}>
              {/* Show additions */}
              {(suggestedPlan.add || suggestedPlan.phases || []).map((phase: any, idx: number) => (
                <Typography key={`add-${idx}`} variant="caption" sx={{ display: 'block', color: '#27ae60' }}>
                  ➕ {phase.name} ({phase.startDate} → {phase.endDate})
                </Typography>
              ))}
              {/* Show removals */}
              {(suggestedPlan.remove || []).map((id: string, idx: number) => (
                <Typography key={`rem-${idx}`} variant="caption" sx={{ display: 'block', color: '#e74c3c' }}>
                  ➖ Ta bort fas: {id}
                </Typography>
              ))}
              {/* Show updates */}
              {(suggestedPlan.update || []).map((phase: any, idx: number) => (
                <Typography key={`upd-${idx}`} variant="caption" sx={{ display: 'block', color: '#3498db' }}>
                  ✏️ Uppdatera: {phase.name || phase.id}
                </Typography>
              ))}
            </Box>
            <Divider sx={{ mb: 1.5 }} />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                size="small"
                startIcon={acceptingPlan ? <CircularProgress size={16} /> : <Check />}
                onClick={handleAcceptPlan}
                disabled={acceptingPlan}
                sx={{ backgroundColor: '#27ae60', flex: 1 }}
              >
                Acceptera
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Refresh />}
                onClick={handleRegeneratePlan}
                disabled={loading}
                sx={{ flex: 1 }}
              >
                Nytt förslag
              </Button>
            </Box>
          </Box>
        )}

        {/* Input */}
        <Box
          sx={{
            p: 2,
            borderTop: '1px solid #e0e0e0',
            display: 'flex',
            gap: 1,
            backgroundColor: '#fafafa',
          }}
        >
          <TextField
            fullWidth
            size="small"
            placeholder="Skriv ditt meddelande..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
            disabled={loading}
            multiline
            maxRows={3}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#fff',
              }
            }}
          />
          <IconButton
            onClick={handleSend}
            disabled={!input.trim() || loading}
            sx={{ 
              backgroundColor: '#9b59b6',
              color: '#fff',
              '&:hover': { backgroundColor: '#8e44ad' },
              '&:disabled': { backgroundColor: '#ccc' },
            }}
          >
            <Send />
          </IconButton>
        </Box>
      </Box>
    </Drawer>
  );
};

export default AIPlanningChat;
